import React, { FC, useCallback, useMemo } from 'react';
import { getIn, useFormikContext } from 'formik';
import { Asset, Button, Modal, PlusIcon, Alert } from '@clearcover/atlas';
import { useLazyQuoteApi } from 'Shared/services/Api';
import { ConfirmationModal, LoadingPage, useToast } from 'Shared/components';
import {
  FormValuesType,
  GetDetailsResponseType,
  transformAppDetails,
  VehicleFieldsType,
} from 'QuoteApp/services/endpoints/GetDetails';
import {
  CreateVehicleResponseType,
  transformCreateVehicleResponse,
} from 'QuoteApp/services/endpoints/PostAddVehicle';
import { useAppContext } from 'QuoteApp/services/providers';
import { formatVehicleDisplay, getErrorMessage } from 'QuoteApp/utils';
import { useEdit } from 'QuoteApp/utils/contexts';
import { Accordion, AccordionItem, AccordionHeader } from '../Accordion';
import { SaveDiscardModal } from '../SaveDiscardModal';
import { VehicleForm } from './VehicleForm';
import styles from './styles.module.css';

export type VehicleDataType = Record<string, VehicleFieldsType>;

type VehiclesListPropsType = {
  onVehiclesPage?: boolean;
  dataCompleteness: Record<string, boolean>;
  editingIdDisplay: string;
};

export const VehiclesList: FC<VehiclesListPropsType> = ({
  dataCompleteness,
  editingIdDisplay,
}) => {
  const { toast } = useToast();
  const { appId, config, refreshRate, setDetails, details } = useAppContext();

  const [{ loading: creatingVehicle }, createVehicle] =
    useLazyQuoteApi<CreateVehicleResponseType>({
      method: 'POST',
      url: `/insurance_applications/${appId}/add_vehicle`,
    });

  const [
    { loading: deletingVehicle, error: deleteVehicleError },
    deleteVehicle,
  ] = useLazyQuoteApi<GetDetailsResponseType>({
    method: 'POST',
  });

  const { editingId, changeEditingId } = useEdit();

  const executeCreateVehicle = useCallback(async () => {
    try {
      const createVehicleResponse = await createVehicle({
        transformResponse: transformCreateVehicleResponse,
      });

      setDetails(createVehicleResponse.data.appDetails);
      changeEditingId(createVehicleResponse.data.id);
    } catch (e) {
      toast({
        type: 'error',
        message: getErrorMessage(e),
        withSecondaryNav: false,
        icon: 'error',
      });
    }
  }, [changeEditingId, createVehicle, setDetails, toast]);

  const {
    resetForm,
    handleSubmit,
    submitForm,
    isSubmitting,
    values,
    dirty,
    errors,
  } = useFormikContext<FormValuesType>();

  const isAddVehicleCommandAvailable = useMemo(
    () =>
      details.commands.insuranceProduct.some(
        (ip) => ip.name === 'add_vehicle' && ip.runnable
      ),
    [details.commands.insuranceProduct]
  );

  const isDeleteVehicleCommandAvailable = (vehicleId: string) => {
    return details.commands.vehicles[vehicleId].commands.some(
      (command) =>
        command.name === 'delete_vehicle' && command.status !== 'not_runnable'
    );
  };

  if (!config) {
    return <LoadingPage />;
  }

  return (
    <div data-testid="vehicles-list">
      <Accordion>
        {Object.keys(values.vehicles)
          .filter((vehicleId) => details.vehicles[vehicleId]) // avoid race condition when deleting vehicle
          .map((vehicleId: string, i) => {
            const vehicle = values.vehicles[vehicleId];
            const label = formatVehicleDisplay(vehicle);
            const vehicleLabel = label === '' ? `Vehicle ${i + 1}` : label;

            return (
              <AccordionItem
                key={vehicleId}
                editable={vehicle.editable}
                actions={(setIsOpen) => (
                  <>
                    {isDeleteVehicleCommandAvailable(vehicleId) && (
                      <Modal
                        variant="normal"
                        toggle={(openModal) => (
                          <Button
                            data-testid={`remove-vehicle-${i + 1}`}
                            variant="danger-secondary"
                            size="sm"
                            disabled={
                              deletingVehicle ||
                              isSubmitting ||
                              Object.keys(values.vehicles).length === 1 ||
                              (editingId !== vehicleId && dirty)
                            }
                            onClick={openModal}
                          >
                            Remove
                          </Button>
                        )}
                      >
                        {(close) => (
                          <ConfirmationModal
                            onSubmit={async () => {
                              deleteVehicle({
                                url: `/insurance_applications/${appId}/vehicles/${vehicleId}/delete`,
                                transformResponse: transformAppDetails,
                              }).then((response) => {
                                refreshRate();
                                changeEditingId(null);
                                setDetails(response.data);
                                close();
                                toast({
                                  message: `${vehicleLabel} has been successfully deleted.`,
                                  type: 'success',
                                  withSecondaryNav: false,
                                });
                              });
                            }}
                            close={close}
                            isSubmitting={deletingVehicle}
                            error={deleteVehicleError?.message}
                            headerText="Please confirm removal"
                            bodyText={`You are about to remove ${vehicleLabel} from this application. Do you wish to continue?`}
                          />
                        )}
                      </Modal>
                    )}
                    {editingId === vehicleId ? (
                      <Button
                        variant="secondary"
                        type="submit"
                        size="sm"
                        disabled={deletingVehicle || isSubmitting}
                        isSubmitting={isSubmitting}
                        onClick={() => handleSubmit()}
                        data-testid={`save-vehicle-${i + 1}-header`}
                        className={styles.saveAction}
                      >
                        Save
                      </Button>
                    ) : dirty ? (
                      <Modal
                        variant="normal"
                        toggle={(openModal) => (
                          <Button
                            data-testid={`edit-vehicle-${i + 1}`}
                            variant="secondary"
                            size="sm"
                            onClick={openModal}
                            className={styles.saveAction}
                          >
                            Edit
                          </Button>
                        )}
                      >
                        {() => (
                          <SaveDiscardModal
                            onSave={async () => {
                              await submitForm();
                              setIsOpen(true);
                              changeEditingId(vehicleId);
                            }}
                            itemDisplay={editingIdDisplay}
                            saveSubmitting={isSubmitting}
                            onDiscard={() => {
                              resetForm();
                              changeEditingId(vehicleId);
                              setIsOpen(true);
                            }}
                          />
                        )}
                      </Modal>
                    ) : (
                      <Button
                        variant="secondary"
                        size="sm"
                        data-testid={`edit-vehicle-${i + 1}`}
                        onClick={() => {
                          changeEditingId(vehicleId);
                          setIsOpen(true);
                        }}
                        className={styles.saveAction}
                      >
                        Edit
                      </Button>
                    )}
                  </>
                )}
                startOpen={editingId === vehicleId}
                identifier={vehicleId}
                header={
                  <AccordionHeader
                    entity="vehicle"
                    header={vehicleLabel}
                    dataComplete={dataCompleteness[vehicleId]}
                  />
                }
                footer={
                  editingId === vehicleId && (
                    <>
                      <Button
                        variant="secondary"
                        disabled={deletingVehicle || isSubmitting}
                        onClick={() => {
                          resetForm();
                          changeEditingId(null);
                        }}
                        data-testid={`cancel-edit-vehicle-${i + 1}`}
                        className={styles.cancelAction}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        type="submit"
                        isSubmitting={isSubmitting}
                        disabled={isSubmitting}
                        onClick={() => handleSubmit()}
                        data-testid={`save-vehicle-${i + 1}-footer`}
                      >
                        Save Vehicle
                      </Button>
                    </>
                  )
                }
              >
                {editingId === vehicleId &&
                  getIn(errors, `vehicles.${editingId}.submissionError`) && (
                    <Alert
                      message={getIn(
                        errors,
                        `vehicles.${editingId}.submissionError`
                      )}
                    />
                  )}
                <VehicleForm
                  appId={appId}
                  config={config.vehicles}
                  vehicleId={vehicleId}
                />
              </AccordionItem>
            );
          })}
      </Accordion>
      <div className={styles.actionsContainer}>
        <div className={styles.actions}>
          {dirty ? (
            <Modal
              variant="normal"
              toggle={(openModal) => (
                <Button
                  data-testid="add-vehicle"
                  variant="secondary"
                  isSubmitting={creatingVehicle}
                  className={styles.primaryBtn}
                  disabled={creatingVehicle || !isAddVehicleCommandAvailable}
                  onClick={() => {
                    openModal();
                  }}
                >
                  <Asset
                    icon={PlusIcon}
                    description="additional-vehicle-plus"
                    height={12}
                    width={12}
                  />
                  Add New Vehicle
                </Button>
              )}
            >
              {(close) => (
                <SaveDiscardModal
                  onSave={async () => {
                    await submitForm();
                    await executeCreateVehicle();
                    close();
                  }}
                  itemDisplay={editingIdDisplay}
                  saveSubmitting={isSubmitting}
                  discardSubmitting={creatingVehicle}
                  onDiscard={async () => {
                    await executeCreateVehicle();
                    close();
                  }}
                />
              )}
            </Modal>
          ) : (
            <Button
              data-testid="add-vehicle"
              variant="secondary"
              isSubmitting={creatingVehicle}
              className={styles.primaryBtn}
              disabled={creatingVehicle || !isAddVehicleCommandAvailable}
              onClick={async () => {
                await executeCreateVehicle();
              }}
            >
              <Asset
                icon={PlusIcon}
                description="additional-vehicle-plus"
                height={12}
                width={12}
              />
              Add New Vehicle
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
